import { useState } from 'react';

import {
  AnimatedChatLoading,
  ChatLoading,
  ChatResponse,
} from 'modules/account/copilot/components';
import {
  useCreateConversation,
  useSendMessage,
} from 'modules/account/copilot/hooks';
import { CiCirclePlus } from 'react-icons/ci';
import { Textarea } from 'ui/components/form';
import { CreateIcon, SidebarIcon } from 'ui/components/icon';
import { NavAction } from 'ui/components/nav';
import UploadListItem from 'ui/partials/Composer/UploadListItem';
import { CHAT_ROLE, SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import useCopilotContext from 'utils/providers/useCopilotContext';
import { useGetLead } from 'modules/account/contact/hooks';
import { useGetDeal } from 'modules/account/sales/hooks';
import { trim } from 'lodash';

import Button from '../Button';
import FileDropZone from '../FileDropZone';
import getPredefinedPrompts from './getPredefinedPrompts';

export default function CopilotChat() {
  const {
    toggleMode,
    newChat,
    isLoading: messageLoading,
    messages,
    topic,
    selectedConversation,
    setSelectedConversation,
    setTopic,
  } = useCopilotContext();
  const [input, setInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [startMessage, setStartMessage] = useState('');

  const { module, recordId } = useCopilotContext();

  const { data: leadData } = useGetLead(
    module === 'leads' ? recordId : undefined,
  );
  const { data: dealData } = useGetDeal(
    module === 'deals' ? recordId : undefined,
  );
  const { mutate: createConversation, isLoading: createLoading } =
    useCreateConversation();
  const { mutate: sendMessage, isLoading: sendMessageLoading } =
    useSendMessage();

  const successCallback = (data: { _id: string; title: string }) => {
    setSelectedConversation(data._id);
    setTopic(data.title ?? '');
  };

  const handleSendPredefinedMessage = (message: string) => {
    createConversation({
      content: message,
      meta: { leadId: leadData?._id, dealId: dealData?._id },
      successCallback,
    });
  };

  const handleSendMessage = () => {
    if (!input.trim()) return;
    // setMessages?.([...messages, { role: 'user', content: input }]);

    if (!messages?.length) {
      createConversation(
        {
          files,
          content: input,
          meta: { leadId: leadData?._id, dealId: dealData?._id },
          successCallback,
        },
        {
          onSuccess: () => {
            setInput('');
          },
        },
      );
    } else {
      sendMessage(
        {
          id: selectedConversation ?? '',
          content: input,
          files,
        },
        {
          onSuccess: () => {
            setInput('');
          },
        },
      );
    }
  };
  const removeAttachment = (name: string) => {
    setFiles((prevVal) => prevVal.filter((val) => !(val.name === name)));
  };

  const filesToUpload = !!files?.length && (
    <div
      className={cn(
        'space-y-[6px]',
        'max-h-[200px]',
        'w-full',
        'overflow-x-hidden',
        'overflow-y-auto',
        files.length > 3 && 'pr-1',
      )}
    >
      {files?.map((file) => (
        <UploadListItem
          key={file.name}
          file={{
            ...file,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          }}
          size={SIZE.Small}
          onRemove={() => removeAttachment(file.name)}
        />
      ))}
    </div>
  );

  const predefinedPrompts = getPredefinedPrompts({ module, recordId }).map(
    (prompt) => (
      <Button
        onClick={() => {
          setStartMessage(prompt);
          handleSendPredefinedMessage(prompt);
        }}
        label={prompt}
        intent={'tertiary'}
        className="py-2 px-3"
      />
    ),
  );

  return (
    <div
      className={cn(
        'flex flex-col',
        'h-full w-full max-w-md',
        'mx-auto p-4 bg-fill-tertiary',
        'rounded-lg shadow-lg',
      )}
    >
      <>
        <div
          className={cn(
            'flex items-center justify-between bg-fill-tertiary pb-[20px]',
            'border-b border-b-separator-primary',
          )}
        >
          <button
            role="button"
            onClick={toggleMode}
            className={cn(
              'group flex gap-2 items-center',
              'hover:text-primary-base cursor-pointer text-sm',
            )}
          >
            <SidebarIcon
              className={cn(
                'w-5 h-5 text-[#7E8B99] group-hover:text-primary-base cursor-pointer',
              )}
            />
            <div>Search History</div>
          </button>
          <button
            role="button"
            onClick={() => {
              newChat();
              setStartMessage('');
            }}
            className={cn(
              'group flex gap-2 items-center',
              'hover:text-primary-base cursor-pointer text-sm',
            )}
          >
            <CreateIcon
              className={cn(
                'w-5 h-5 text-[#7E8B99] group-hover:text-primary-base cursor-pointer',
              )}
            />
            <div>New Chat</div>
          </button>
        </div>
        <div className="text-lg font-semibold text-center py-7">
          {trim(topic, '"') || 'What can I help with?'}
        </div>
        {messages?.length === 0 ? (
          <div className="flex flex-wrap gap-2 justify-center">
            {predefinedPrompts}
          </div>
        ) : null}
        {messageLoading ? (
          <ChatLoading />
        ) : (
          <div className="h-full overflow-y-auto border-b border-separator-primary space-y-2 py-4">
            {messages?.map((msg, index) => (
              <ChatResponse
                key={index}
                role={msg.role}
                content={msg.content}
                type="sidebar"
              />
            ))}
            {createLoading && !messages?.length && (
              <div className="flex-1">
                <ChatResponse
                  role={CHAT_ROLE.User}
                  content={startMessage}
                  type="sidebar"
                />
                <AnimatedChatLoading />
              </div>
            )}
          </div>
        )}

        {messages.length ? (
          <FileDropZone
            onDrop={(_acceptedFiles) => {
              setFiles((prevVal) => [...prevVal, ..._acceptedFiles]);
            }}
          >
            {({ open }) => (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="flex flex-col mt-2 space-y-2"
              >
                <div className="relative">
                  <div
                    className={cn(
                      'flex space-x-2 absolute inset-x-0 bottom-0 p-2 pb-2 justify-between',
                    )}
                  >
                    <NavAction
                      disabled={createLoading || sendMessageLoading}
                      label={
                        <CiCirclePlus className="w-7 h-7 text-[#7E8B99]" />
                      }
                      options={[
                        { text: 'Upload File/s', onClick: open, key: 'upload' },
                      ]}
                    />
                    <Button
                      label={createLoading ? '...' : 'Send'}
                      type="submit"
                      disabled={createLoading || sendMessageLoading}
                      className="p-3"
                    ></Button>
                  </div>
                  <Textarea
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    textAreaClassName="mb-10 scrollbar-none"
                    rows={3}
                    value={input}
                    onChange={(val) => setInput(val)}
                    className="p-2 border rounded-md border-separator-primary  "
                    placeholder="Type a message..."
                  />
                </div>
                <div>{filesToUpload}</div>
              </form>
            )}
          </FileDropZone>
        ) : null}
      </>
    </div>
  );
}
